import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import { toast } from '../utils/toast';

/**
 * Shared Billing Plan editor — supports both Contracts and Estimates.
 *
 * Props:
 *   - ownerType   : 'contract' | 'estimate'
 *   - ownerId     : UUID of the parent row
 *   - totalValue  : header value for % computations (contract.total_value
 *                   or estimate.total_billing)
 *   - currency    : display currency (₹, $, …)
 *   - readOnly    : disable all inputs
 *   - saverRef    : optional ref; parent can call current() to flush unsaved
 *                   milestones without a separate click
 *
 * Invoice release is only available for contracts. For estimates the plan
 * is a template that gets copied to the contract when the contract is
 * created from the estimate.
 */
export default function BillingPlanEditor({ ownerType, ownerId, totalValue, currency, readOnly, saverRef }) {
  const [plan, setPlan] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const urls = urlsFor(ownerType, ownerId);
  // Compact label for the Item column. The full owner ("Contract header"
  // / "Estimate header") was clunky inside a narrow dropdown; "Header"
  // is shorter and unambiguous in context.
  const ownerHeaderLabel = 'Header';
  const canRelease = ownerType === 'contract';
  // On an estimate this plan is only a template; block-release, offset days,
  // the block checkbox and the status pill are all Contract-time concerns.
  // Hide those columns when we're editing an estimate.
  const showReleaseCols = ownerType === 'contract';

  const load = useCallback(async () => {
    if (!ownerId) { setLoading(false); return; }
    try {
      const [bpRes, liRes] = await Promise.all([
        apiFetch(urls.plan),
        apiFetch(urls.lines),
      ]);
      if (bpRes.ok) {
        const d = await bpRes.json();
        setPlan(d.plan || null);
        setMilestones(d.milestones || []);
      }
      if (liRes.ok) {
        const d = await liRes.json();
        // Contracts return { lineItems: [...] }; estimates return { lines: [...] }
        const raw = d.lineItems || d.lines || [];
        setLineItems(raw.map(normaliseLineItem));
      }
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [ownerId, urls.plan, urls.lines]);
  useEffect(() => { load(); }, [load]);

  // Plan-level type. Defaults to 'Milestone' for legacy plans where the model
  // wasn't set explicitly. Stored on billing_plans.model.
  const planType = (plan && plan.model === 'Recurring') ? 'Recurring' : 'Milestone';
  async function setPlanType(next) {
    if (next === planType) return;
    // A plan is either Milestone OR Recurring — never both. The two modes use
    // different row schemas, so existing rows must be cleared on switch to
    // avoid stale fields (like a Milestone's fixed_value polluting the
    // Recurring view's Total Value).
    if (milestones.length > 0) {
      const ok = await toast.confirm({
        title: `Switch to ${next} plan?`,
        body: `This will remove the ${milestones.length} ${planType.toLowerCase()} row(s) currently on this plan. ${next} rows use a different field set.`,
        danger: true, confirmLabel: 'Switch & Clear',
      });
      if (!ok) return;
    }
    setDirty(true);
    setPlan(prev => ({ ...(prev || {}), model: next }));
    setMilestones([]);
  }

  function addMilestone() {
    setDirty(true);
    const nextSeq = (milestones.length + 1) * 10;
    setMilestones(prev => [...prev, {
      _new: true, id: '_new_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
      seq: nextSeq,
      bill_number: ordinal(prev.filter(m => (m.billing_type || planType) === 'Milestone').length + 1),
      billing_type: planType,
      description: '',
      pct_value: 0,
      fixed_value: null,
      tentative_billing_date: null,
      line_item_id: null,
      recurrence: null,
      signoff_required: false,
      billing_block: true,
      block_reason: '',
      status: 'planned',
      bill_date_basis: 'block_release',
      bill_date_offset_days: 0,
      // Recurring-mode defaults
      total_value: null,
      number_of_bills: null,
      amount_per_bill: null,
      upcoming_bill_date: null,
    }]);
  }
  function patch(id, changes) { setDirty(true); setMilestones(prev => prev.map(m => m.id === id ? { ...m, ...changes } : m)); }
  function remove(id)         { setDirty(true); setMilestones(prev => prev.filter(m => m.id !== id)); }

  const save = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setSaving(true);
    try {
      const rows = milestones.map(m => {
        const out = { ...m, id: String(m.id || '').startsWith('_new_') ? undefined : m.id };
        // For Recurring rows, recompute amount_per_bill on save so the persisted
        // value always matches the displayed (floored) one.
        if (planType === 'Recurring') {
          const base = m.line_item_id
            ? (parseFloat((lineItems.find(li => li.id === m.line_item_id) || {}).amount) || 0)
            : (parseFloat(totalValue) || 0);
          const tv = parseFloat(m.total_value);
          const total = Number.isFinite(tv) && tv > 0 ? tv : base;
          const nBills = parseInt(m.number_of_bills, 10) || 0;
          out.amount_per_bill = nBills > 0 ? Math.floor(total / nBills) : null;
        }
        return out;
      });
      const liIds = [...new Set(rows.map(r => r.line_item_id).filter(Boolean))];
      const planPatch = {
        model: planType,                                            // ← plan-level type
        scope: liIds.length === 1 ? 'line_item' : 'contract',
        line_item_id: liIds.length === 1 ? liIds[0] : null,
        name: plan ? plan.name : 'Billing Plan',
      };
      const r = await apiFetch(urls.plan, {
        method: 'POST',
        body: JSON.stringify({ plan: planPatch, milestones: rows }),
      });
      if (r.ok) {
        if (!silent) toast.success('Billing plan saved');
        setDirty(false);
        load();
        if (!silent) setSaving(false);
        return { ok: true };
      } else {
        const d = await r.json().catch(() => ({}));
        // Reconciliation errors arrive as { error, details: [{ message, ... }] }.
        // Render every detail line so the user sees exactly which parent/child
        // pair is off and by how much.
        const lines = Array.isArray(d.details) && d.details.length
          ? d.details.map(x => '• ' + (x.message || JSON.stringify(x))).join('\n')
          : '';
        const msg = lines ? `${d.error || 'Save failed'}\n\n${lines}` : (d.error || 'Billing plan save failed');
        if (!silent) { toast.error(msg); setSaving(false); }
        return { ok: false, error: msg };
      }
    } catch (e) {
      if (!silent) { toast.error(e.message); setSaving(false); }
      return { ok: false, error: e.message };
    }
  }, [urls.plan, milestones, plan, load]);

  useEffect(() => {
    if (!saverRef) return;
    if (readOnly) { saverRef.current = null; return; }
    saverRef.current = async () => {
      if (milestones.length === 0 && !dirty) return { ok: true };
      return save({ silent: true });
    };
    return () => { if (saverRef) saverRef.current = null; };
  }, [saverRef, readOnly, milestones.length, dirty, save]);

  async function release(m) {
    const ok = await toast.confirm({
      title: 'Release billing block?',
      body: `Generate an invoice for "${m.description || m.bill_number || 'this milestone'}". The invoice amount will be computed from ${m.line_item_id ? 'the line-item' : 'the contract'} value.`,
      confirmLabel: 'Release & Invoice',
    });
    if (!ok) return;
    try {
      const r = await apiFetch(`${urls.plan}/milestones/${m.id}/release`, { method: 'PUT' });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(`Invoice ${d.invoice_number} for ${currency || '₹'} ${Number(d.amount).toLocaleString('en-IN')} created`);
        load();
      } else { toast.error(d.error || 'Release failed'); }
    } catch (e) { toast.error(e.message); }
  }

  const msPct = milestones.filter(m => (m.billing_type || 'Milestone') === 'Milestone')
    .reduce((s, m) => s + (parseFloat(m.pct_value) || 0), 0);

  if (loading) return <div style={{ color: '#94A3B8', fontSize: 12 }}>Loading plan…</div>;

  return (
    // minWidth: 0 + maxWidth: 100% are critical: without them this flex
    // column inherits min-width:auto, which lets the wide milestone table
    // push the parent (and therefore the whole contract editor page) past
    // its max-width on monitors narrower than the table's natural size.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0, maxWidth: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* Plan Type toggle. Switching here flips the table layout below
              and stamps every existing row's billing_type to match. */}
          <div style={{ display: 'inline-flex', borderRadius: 7, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.10)' }}>
            {['Milestone', 'Recurring'].map(t => (
              <button
                key={t}
                disabled={readOnly}
                onClick={() => setPlanType(t)}
                style={{
                  padding: '7px 14px', fontSize: 12, fontWeight: 700,
                  border: 'none', cursor: readOnly ? 'default' : 'pointer',
                  background: planType === t ? 'linear-gradient(135deg, #FF00E5, #BF5FFF)' : 'rgba(255,255,255,0.04)',
                  color: planType === t ? '#fff' : '#94A3B8',
                  letterSpacing: '0.4px', textTransform: 'uppercase',
                }}
              >{t}</button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#94A3B8' }}>
            {milestones.length === 0
              ? (ownerType === 'estimate'
                  ? 'No rows yet — this plan will be copied to the contract when one is created from this estimate.'
                  : 'No rows yet — create one to generate invoices.')
              : (planType === 'Milestone'
                  ? <>Total of Milestones: <strong style={{ color: msPct === 100 ? '#10B981' : msPct > 100 ? '#EF4444' : '#F59E0B' }}>{msPct.toFixed(2)}%</strong></>
                  : <>{milestones.length} recurring stream{milestones.length === 1 ? '' : 's'}</>)}
          </div>
        </div>
        {!readOnly && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={addMilestone} style={btnGhost}>+ Add row</button>
            <button onClick={() => save()} disabled={saving} style={btnPrimary}>
              {saving ? '…' : (dirty ? 'Save Plan *' : 'Save Plan')}
            </button>
          </div>
        )}
      </div>

      {milestones.length === 0 ? null : planType === 'Milestone' ? (
        // ── Milestone-mode table ──────────────────────────────────────────
        // Wrapper takes 100% of the section width; the inner table sizes
        // to its own natural content (min-width: max-content). Together
        // they put the horizontal scrollbar INSIDE this wrapper instead
        // of letting the wide milestone table push the page.
        <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
          <table style={{ minWidth: 'max-content', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr>
                <Th>Seq</Th><Th>Bill #</Th><Th>Item</Th>
                <Th>Description</Th><Th align="right">% of value</Th>
                <Th align="right">Fixed</Th>
                <Th align="right">Value</Th>
                <Th>Tentative date</Th>
                {showReleaseCols && <Th>Bill date basis</Th>}
                {showReleaseCols && <Th align="right">Offset (days)</Th>}
                {showReleaseCols && <Th align="center">Block</Th>}
                {showReleaseCols && <Th align="center">Status</Th>}
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {milestones.map(m => {
                const base = m.line_item_id
                  ? (parseFloat((lineItems.find(li => li.id === m.line_item_id) || {}).amount) || 0)
                  : totalValue;
                const amount = m.fixed_value ? parseFloat(m.fixed_value) : Math.round(base * (parseFloat(m.pct_value) || 0) / 100 * 100) / 100;
                return (
                  <tr key={m.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <Td><input type="number" value={m.seq ?? 0} onChange={e => patch(m.id, { seq: parseInt(e.target.value, 10) || 0 })} style={{ ...inp, width: 54 }} /></Td>
                    <Td><input value={m.bill_number || ''} onChange={e => patch(m.id, { bill_number: e.target.value })} style={{ ...inp, width: 70 }} placeholder="1st" /></Td>
                    <Td>
                      <select value={m.line_item_id || ''} onChange={e => patch(m.id, { line_item_id: e.target.value || null })} style={{ ...inp, width: 110 }}
                        title={(() => {
                          if (!m.line_item_id) return 'Header';
                          const li = lineItems.find(x => x.id === m.line_item_id);
                          return li ? `Item ${lineItems.indexOf(li) + 1} — ${li.label}` : 'Header';
                        })()}>
                        <option value="">{ownerHeaderLabel}</option>
                        {/* Compact "Item N" labels — full item name shown via the
                            select's title tooltip on hover. Keeps the column narrow
                            and the milestone table readable. */}
                        {lineItems.map((li, idx) => (
                          <option key={li.id} value={li.id} title={li.label}>{`Item ${idx + 1}`}</option>
                        ))}
                      </select>
                    </Td>
                    <Td><input value={m.description || ''} onChange={e => patch(m.id, { description: e.target.value })} style={{ ...inp, width: 220 }} placeholder="e.g. Requirement Workshop" /></Td>
                    <Td align="right"><input type="number" step="0.01" value={m.pct_value ?? 0} onChange={e => patch(m.id, { pct_value: parseFloat(e.target.value) || 0 })} style={{ ...inp, width: 80, textAlign: 'right' }} /></Td>
                    <Td align="right"><input type="number" step="0.01" value={m.fixed_value ?? ''} onChange={e => patch(m.id, { fixed_value: e.target.value === '' ? null : parseFloat(e.target.value) })} style={{ ...inp, width: 90, textAlign: 'right' }} placeholder="—" /></Td>
                    {/* Calculated value column — fixed_value if set, else
                        base × pct_value / 100. base = the line item amount
                        when scoped to a line, otherwise the pricing base
                        passed in via totalValue (negotiated → total). */}
                    <Td align="right" style={{ fontFamily: 'ui-monospace, monospace', color: '#10B981', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {(currency || '₹') + ' ' + Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </Td>
                    <Td>
                      <input type="date" value={m.tentative_billing_date ? String(m.tentative_billing_date).slice(0, 10) : ''}
                        onChange={e => patch(m.id, { tentative_billing_date: e.target.value || null })}
                        style={{ ...inp, width: 130 }} />
                    </Td>
                    {showReleaseCols && (
                      <Td>
                        <select value={m.bill_date_basis || 'block_release'}
                          onChange={e => patch(m.id, { bill_date_basis: e.target.value })}
                          style={{ ...inp, width: 180 }}
                          title="When the invoice should be dated once the block is released">
                          <option value="block_release">Date of block removal</option>
                          <option value="end_of_current_month">Last day of current month</option>
                          <option value="beginning_of_next_month">Beginning of next month</option>
                        </select>
                      </Td>
                    )}
                    {showReleaseCols && (
                      <Td align="right">
                        <input type="number" min={-30} max={30} step={1}
                          value={m.bill_date_offset_days ?? 0}
                          onChange={e => {
                            const n = parseInt(e.target.value, 10);
                            const clamped = Number.isFinite(n) ? Math.max(-30, Math.min(30, n)) : 0;
                            patch(m.id, { bill_date_offset_days: clamped });
                          }}
                          style={{ ...inp, width: 70, textAlign: 'right' }}
                          title="Shift the basis by ±30 days" />
                      </Td>
                    )}
                    {showReleaseCols && (
                      <Td align="center">
                        <label title={m.billing_block ? 'Billing is blocked — invoice cannot be generated yet' : 'Released — invoice can be generated'} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <input type="checkbox" checked={!!m.billing_block} onChange={e => patch(m.id, { billing_block: e.target.checked })} />
                        </label>
                      </Td>
                    )}
                    {showReleaseCols && (
                      <Td align="center">
                        <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase',
                          background: m.invoice_id ? 'rgba(16,185,129,0.18)' : m.billing_block ? 'rgba(245,158,11,0.18)' : 'rgba(59,141,248,0.18)',
                          color:      m.invoice_id ? '#10B981' : m.billing_block ? '#F59E0B' : '#60A5FA',
                        }}>{m.invoice_id ? 'Invoiced' : m.billing_block ? 'Blocked' : 'Ready'}</span>
                      </Td>
                    )}
                    <Td align="right" style={{ whiteSpace: 'nowrap' }}>
                      {canRelease && !m.invoice_id && !m.billing_block && (
                        <button onClick={() => release(m)} style={btnApprove} title={`Generate invoice for ${currency || '₹'} ${amount.toLocaleString('en-IN')}`}>Invoice</button>
                      )}
                      {!readOnly && (
                        <button onClick={() => remove(m.id)} style={{ ...linkBtn, color: '#FCA5A5', marginLeft: 10 }}>Remove</button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        // ── Recurring-mode table ─────────────────────────────────────────
        // Each row defines one recurring stream that produces N bills:
        // total_value / number_of_bills = amount_per_bill, with the first
        // bill landing on (basis + offset) days, subsequent ones repeating
        // by the chosen cadence.
        <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
          <table style={{ minWidth: 'max-content', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr>
                <Th>Seq</Th>
                <Th>Item</Th>
                <Th>Description</Th>
                <Th align="right">Total Value</Th>
                <Th align="right"># Bills</Th>
                <Th align="right">Amount / Bill</Th>
                <Th>Bill date basis</Th>
                <Th align="right">Offset (days)</Th>
                <Th>Upcoming bill</Th>
                {showReleaseCols && <Th align="center">Block</Th>}
                {showReleaseCols && <Th align="center">Status</Th>}
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {milestones.map(m => {
                // Total Value comes from the chosen line item's amount, or
                // the contract/estimate header value when the row is scoped
                // to "Header". An override on m.total_value wins (lets users
                // tweak); fixed_value is intentionally ignored — it is a
                // Milestone concept and would mislead Recurring math.
                const base = m.line_item_id
                  ? (parseFloat((lineItems.find(li => li.id === m.line_item_id) || {}).amount) || 0)
                  : totalValue;
                const totalNum = parseFloat(m.total_value);
                const total = Number.isFinite(totalNum) && totalNum > 0 ? totalNum : base;
                const nBills = parseInt(m.number_of_bills, 10) || 0;
                // Floor to nearest lower integer — accounting prefers a whole
                // number per invoice; any fractional remainder shows up on the
                // last bill via reconciliation if needed.
                const perBill = nBills > 0 ? Math.floor(total / nBills) : 0;
                return (
                  <tr key={m.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <Td><input type="number" value={m.seq ?? 0} onChange={e => patch(m.id, { seq: parseInt(e.target.value, 10) || 0 })} style={{ ...inp, width: 54 }} /></Td>
                    <Td>
                      <select value={m.line_item_id || ''}
                        onChange={e => patch(m.id, { line_item_id: e.target.value || null, total_value: null })}
                        style={{ ...inp, width: 110 }}
                        title={(() => {
                          if (!m.line_item_id) return 'Header';
                          const li = lineItems.find(x => x.id === m.line_item_id);
                          return li ? `Item ${lineItems.indexOf(li) + 1} — ${li.label}` : 'Header';
                        })()}>
                        <option value="">{ownerHeaderLabel}</option>
                        {lineItems.map((li, idx) => (
                          <option key={li.id} value={li.id} title={li.label}>{`Item ${idx + 1}`}</option>
                        ))}
                      </select>
                    </Td>
                    <Td><input value={m.description || ''} onChange={e => patch(m.id, { description: e.target.value })} style={{ ...inp, width: 220 }} placeholder="e.g. Monthly AMS retainer" /></Td>
                    <Td align="right" style={{ fontFamily: 'ui-monospace, monospace', color: '#10B981', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {(currency || '₹') + ' ' + Number(total || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </Td>
                    <Td align="right">
                      <input type="number" min="1" step="1" value={m.number_of_bills ?? ''}
                        onChange={e => patch(m.id, { number_of_bills: e.target.value === '' ? null : parseInt(e.target.value, 10) || 0 })}
                        style={{ ...inp, width: 70, textAlign: 'right' }}
                        placeholder="12" />
                    </Td>
                    <Td align="right" style={{ fontFamily: 'ui-monospace, monospace', color: '#3B8FF4', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {(currency || '₹') + ' ' + Number(perBill || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </Td>
                    <Td>
                      <select value={m.bill_date_basis || 'block_release'}
                        onChange={e => patch(m.id, { bill_date_basis: e.target.value })}
                        style={{ ...inp, width: 200 }}
                        title="Anchor date the recurring stream starts from">
                        <option value="contract_start">Contract start date</option>
                        <option value="contract_end">Contract end date</option>
                        <option value="item_start">Line item start date</option>
                        <option value="item_end">Line item end date</option>
                        <option value="end_of_current_month">Last day of current month</option>
                        <option value="beginning_of_next_month">Beginning of next month</option>
                        <option value="block_release">Date of block removal</option>
                        <option value="specific">Specific date (Upcoming bill →)</option>
                      </select>
                    </Td>
                    <Td align="right">
                      <input type="number" min={-30} max={365} step={1}
                        value={m.bill_date_offset_days ?? 0}
                        onChange={e => patch(m.id, { bill_date_offset_days: parseInt(e.target.value, 10) || 0 })}
                        style={{ ...inp, width: 70, textAlign: 'right' }}
                        title="Days to add to the basis date" />
                    </Td>
                    <Td>
                      <input type="date"
                        value={m.upcoming_bill_date ? String(m.upcoming_bill_date).slice(0, 10) : ''}
                        onChange={e => patch(m.id, { upcoming_bill_date: e.target.value || null })}
                        style={{ ...inp, width: 130 }} />
                    </Td>
                    {showReleaseCols && (
                      <Td align="center">
                        <input type="checkbox" checked={!!m.billing_block} onChange={e => patch(m.id, { billing_block: e.target.checked })}
                          title={m.billing_block ? 'Stream blocked' : 'Stream active'} />
                      </Td>
                    )}
                    {showReleaseCols && (
                      <Td align="center">
                        <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase',
                          background: m.billing_block ? 'rgba(245,158,11,0.18)' : 'rgba(59,141,248,0.18)',
                          color:      m.billing_block ? '#F59E0B' : '#60A5FA',
                        }}>{m.billing_block ? 'Blocked' : 'Active'}</span>
                      </Td>
                    )}
                    <Td align="right" style={{ whiteSpace: 'nowrap' }}>
                      {!readOnly && (
                        <button onClick={() => remove(m.id)} style={{ ...linkBtn, color: '#FCA5A5', marginLeft: 10 }}>Remove</button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* Build endpoint paths for the given owner. Keep logic here so the
   component body stays tidy. */
function urlsFor(ownerType, ownerId) {
  if (ownerType === 'estimate') {
    return {
      plan:  `/api/estimates/effort-estimates/${ownerId}/billing-plan`,
      lines: `/api/estimates/effort-estimates/${ownerId}/lines`,
    };
  }
  return {
    plan:  `/api/contracts/${ownerId}/billing-plan`,
    lines: `/api/contracts/${ownerId}/line-items`,
  };
}

/* Normalise line rows from either contract_line_items or effort_estimate_lines
   into a common { id, seq, label, amount } shape used inside the table. */
function normaliseLineItem(li) {
  return {
    id:     li.id,
    seq:    li.seq ?? li.item_number ?? 0,
    label:  li.item_name || li.service_description || li.phase || li.role || 'Line item',
    amount: parseFloat(li.amount != null ? li.amount : li.extended_billing) || 0,
  };
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function Th({ children, align }) { return <th style={{ textAlign: align || 'left', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: '#94A3B8', letterSpacing: '0.4px', padding: '8px 6px' }}>{children}</th>; }
function Td({ children, align, style }) { return <td style={{ padding: '6px', textAlign: align || 'left', ...(style || {}) }}>{children}</td>; }

const inp = { padding: '6px 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', fontSize: 12, outline: 'none' };
const btnPrimary = { padding: '8px 14px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #FF00E5, #BF5FFF)', color: '#fff' };
const btnGhost   = { padding: '8px 12px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', cursor: 'pointer' };
const btnApprove = { padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #10B981, #3B8FF4)', color: '#fff' };
const linkBtn    = { all: 'unset', cursor: 'pointer', color: '#60A5FA', fontSize: 11.5 };
